import {
  API_ERROR_CODES,
  parseApiError,
} from "@/lib/api/api-error";

export type VerifyEmailStatus =
  | "loading"
  | "success"
  | "invalid"
  | "expired"
  | "already_verified"
  | "error";

const VERIFY_EMAIL_MESSAGES = {
  network:
    "We couldn't reach the server. Check your connection and try again.",
  invalid: "This verification link is invalid. Request a new one.",
  expired: "This verification link has expired. Request a new one.",
  alreadyVerified:
    "This email address is already verified. You can log in.",
  generic: "Something went wrong. Please try again later.",
} as const;

export const VERIFY_EMAIL_SUCCESS_MESSAGE =
  "Your email has been verified. You can log in now.";

export const RESEND_VERIFICATION_SUCCESS_MESSAGE =
  "If an account exists for that email and still needs verification, we sent a new link.";

export function resolveVerifyEmailResult(err: unknown): {
  status: Exclude<VerifyEmailStatus, "loading" | "success">;
  message: string;
} {
  const { status, code, message, isNetworkError } = parseApiError(err);

  if (isNetworkError) {
    return { status: "error", message: VERIFY_EMAIL_MESSAGES.network };
  }

  if (status === 409 || code === API_ERROR_CODES.alreadyVerified) {
    return {
      status: "already_verified",
      message: message ?? VERIFY_EMAIL_MESSAGES.alreadyVerified,
    };
  }

  if (
    status === 400 &&
    (code === API_ERROR_CODES.expiredVerificationToken ||
      code === API_ERROR_CODES.malformedVerificationToken)
  ) {
    return {
      status: "expired",
      message: message ?? VERIFY_EMAIL_MESSAGES.expired,
    };
  }

  if (status === 400 && code === API_ERROR_CODES.invalidVerificationToken) {
    return {
      status: "invalid",
      message: message ?? VERIFY_EMAIL_MESSAGES.invalid,
    };
  }

  if (status === 400) {
    return {
      status: "invalid",
      message: message ?? VERIFY_EMAIL_MESSAGES.invalid,
    };
  }

  return { status: "error", message: VERIFY_EMAIL_MESSAGES.generic };
}

export function shouldOfferResendVerification(
  status: VerifyEmailStatus,
): boolean {
  return status === "invalid" || status === "expired" || status === "error";
}

export function getResendVerificationErrorMessage(err: unknown): string {
  const { message, isNetworkError } = parseApiError(err);

  if (isNetworkError) {
    return VERIFY_EMAIL_MESSAGES.network;
  }

  return message ?? VERIFY_EMAIL_MESSAGES.generic;
}
